/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import MockAdapter from 'axios-mock-adapter';
import { delayedResponse, makeMockResponse } from '../mockUtils';
import {
    AlertDto,
    AlertsListDto,
    AlertsSearchParams,
    AlertItemsSearchParams,
    AlertsSortType,
    AlertItemsListDto,
    AlertTemplateDTO,
    TemplatesListSearchParams,
    TemplatesList,
    TemplatesListSortType,
    ALERT_NAME,
    TemplateExecutionListDTO,
    TemplateRuleDto,
    CreateTemplateRulesParams,
    CreateTemplateRuleDto,
    TemplateRulesListDto,
    TemplateRulesListSearchParams,
    UpdateTemplateRuleDto,
    RuleType,
    TemplateRuleEntityType,
} from './alertsClient.typings';
import {
    alertItemsDtoFactory,
    alertDtoFactory,
    alertItemContentFactoryByType,
    alertTemplateFactory,
    alertTemplateExecution,
    alertTemplateRuleFactory,
} from './alertFactories';
import { Observable, interval } from 'rxjs';
import { tap, mapTo } from 'rxjs/operators';
import moment from 'moment';
import { sortByProp } from '../../../utils/utils';
import { sample } from 'lodash';

const ALERTS_URL = /\/alerts$/;
const ALERT_ITEMS_URL = /\/alerts\/(\d+)\/items/;
const UPDATE_ALERTS_URL = /\/alerts$/;
const GET_ALERT_DETAILS_URL = /\/alerts\/\d+/;
const UPDATE_ALERT_ITEM_URL = /\/alerts\/\d+\/items\/\d+/;
const ALERTS_TEMPLATES_URL = /\/alerts\/templates/;
const UPDATE_ALERTS_TEMPLATES_URL = /\/alerts\/templates\/\d+/;
const GET_EXECUTIONS_LIST = /\/alerts\/templates\/\d+\/executions/;
const TEMPLATE_RULES_URL = /\/alerts\/types(\/?$|\/(?!taxNumbersList).*)/;
const TEMPLATE_RULES_TAX_NUMBERS_LIST_URL = /\/alerts\/types\/taxNumbersList$/;

export function addAlertsMockApi(mockAdapter: MockAdapter): void {
    const alertsBackend = new AlertsBackendStore({
        total: 60,
        createAlertEveryMs: 20000,
    });

    const alertsTemplatesBackend = new AlertsTemplatesBackendStore({
        total: ALERT_NAME.length,
    });

    const alertsTemplateRulesBackend = new AlertsTemplateRulesBackendStore({ total: 40 });

    mockAdapter.onGet(ALERTS_URL).reply((config) => {
        const data = alertsBackend.getAlerts(config.params);

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(ALERT_ITEMS_URL).reply((config) => {
        const alertId = parseInt(config.url.match(ALERT_ITEMS_URL)[1]);
        const alertDto = alertsBackend.getAlert(alertId);
        const code = alertDto ? 200 : 404;
        const params = config.params as AlertItemsSearchParams;
        const count = alertDto.itemsCount;

        const offset = params.offset * params.limit;
        const items = alertItemsDtoFactory
            .list({
                count,
            })
            .map((item) => {
                item.content = alertItemContentFactoryByType[alertDto.type.entityType].item();
                return item;
            })
            .slice(offset, offset + params.limit);

        const data: AlertItemsListDto = {
            count,
            items,
        };

        return delayedResponse(makeMockResponse(code, data, config), Math.random() * 100);
    });

    mockAdapter.onPut(UPDATE_ALERT_ITEM_URL).reply((config) => {
        const alertItemDto = JSON.parse(config.data);

        return delayedResponse(makeMockResponse(200, alertItemDto, config), Math.random() * 100);
    });

    mockAdapter.onGet(GET_EXECUTIONS_LIST).reply((config) => {
        const executionsCount = 35;
        const offset = config.params.offset * config.params.limit;

        const executionsList: TemplateExecutionListDTO = {
            count: executionsCount,
            alertsChecksExecutions: alertTemplateExecution
                .list({ count: executionsCount })
                .slice(offset, offset + config.params.limit),
        };

        return delayedResponse(makeMockResponse(200, executionsList, config), Math.random() * 100);
    });

    mockAdapter.onPost(UPDATE_ALERTS_URL).reply((config) => {
        const data = alertsBackend.updateAlerts(JSON.parse(config.data));

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(GET_ALERT_DETAILS_URL).reply((config) => {
        const alertId = parseInt(config.url.split('/')[2]);
        const data = alertsBackend.getAlert(alertId);

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onGet(ALERTS_TEMPLATES_URL).reply((config) => {
        const data = alertsTemplatesBackend.getTemplates(config.params);

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 100);
    });

    mockAdapter.onPost(UPDATE_ALERTS_TEMPLATES_URL).reply((config) => {
        const templateDto = JSON.parse(config.data);

        return delayedResponse(makeMockResponse(200, templateDto, config), Math.random() * 100);
    });

    mockAdapter.onGet(TEMPLATE_RULES_URL).reply((config) => {
        const data = alertsTemplateRulesBackend.getAlertTemplateRules(config.params);

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 2000);
    });

    mockAdapter.onPost(TEMPLATE_RULES_URL).reply((config) => {
        alertsTemplateRulesBackend.createAlertTemplateRules(config.data);

        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 2000);
    });

    mockAdapter.onPatch(TEMPLATE_RULES_URL).reply((config) => {
        const ruleId = +config.url.split('/')[5];

        alertsTemplateRulesBackend.updateTemplateRule(ruleId, config.data);

        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 1000);
    });

    mockAdapter.onDelete(TEMPLATE_RULES_URL).reply((config) => {
        alertsTemplateRulesBackend.deleteTemplateRules(config.data, false);

        return delayedResponse(makeMockResponse(200, {}, config), Math.random() * 1000);
    });

    mockAdapter.onPost(TEMPLATE_RULES_TAX_NUMBERS_LIST_URL).reply((config) => {
        const body = JSON.parse(config.data);

        const arr = Array(body.limit).fill(0);

        const data = arr.map(() => body.taxNumber + (Math.random() * 100000).toFixed());

        return delayedResponse(makeMockResponse(200, data, config), Math.random() * 1000);
    });
}

export class AlertsBackendStore {
    private alerts: AlertDto[] = [];
    private newAlertCreatedTime: moment.Moment = moment('2021-08-30T00:00:00.000Z');

    constructor(options: { total: number; createAlertEveryMs: number }) {
        for (let index = 0; index < options.total; index++) {
            this.createAlert();
        }

        if (options.createAlertEveryMs) {
            this.initAlertsCreationTask(options.createAlertEveryMs).subscribe();
        }
    }

    public getAlerts(params: AlertsSearchParams): AlertsListDto {
        let alerts = this.alerts.slice();

        // simple sorting
        if ((params.sort as unknown as AlertsSortType[]).includes('inserted,desc')) {
            alerts.reverse();
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('status,desc' || 'status,desc')) {
            alerts = sortByProp(alerts, 'status');
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('assigneeUserId,desc')) {
            alerts = alerts.sort((a, b) => a.assigneeUser.id - b.assigneeUser.id);
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('assigneeUserId,asc')) {
            alerts = alerts.sort((a, b) => b.assigneeUser.id - a.assigneeUser.id);
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('itemsCount,desc')) {
            alerts = alerts.sort((a, b) => a.itemsCount - b.itemsCount);
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('itemsCount,asc')) {
            alerts = alerts.sort((a, b) => b.itemsCount - a.itemsCount);
        }

        if ((params.sort as unknown as AlertsSortType[]).includes('type.priority,asc')) {
            alerts.reverse();
        }

        if (
            (params.sort as unknown as AlertsSortType[]).includes('id,asc') &&
            (params.sort as unknown as AlertsSortType[]).length === 1
        ) {
            alerts.reverse();
        }

        // simple filtering

        if (params.dateFrom) {
            alerts = alerts.filter((alert) => {
                return moment(alert.inserted).isAfter(params.dateFrom);
            });
        }

        if (params.status.length !== 0) {
            alerts = alerts.filter((alert) => {
                return params.status.includes(alert.status);
            });
        }

        if (params.priority.length !== 0) {
            alerts = alerts.filter((alert) => {
                return params.priority.includes(alert.type.priority);
            });
        }

        if (params.type.length !== 0) {
            alerts = alerts.filter((alert) => {
                return params.type.includes(alert.type.name);
            });
        }

        if (params.assigneeUserId.length !== 0) {
            alerts = alerts.filter((alert) => {
                return params.assigneeUserId.includes(alert.assigneeUser.id);
            });
        }

        const offset = params.offset * params.limit;

        return {
            count: alerts.length,
            alerts: alerts.slice(offset, offset + params.limit).map((alert) => ({ ...alert })),
        };
    }

    public getAlert(id: AlertDto['id']): AlertDto {
        const alert = this.alerts.find((a) => a.id === id);

        return alert && { ...alert };
    }

    public updateAlerts(alerts: AlertDto[]) {
        for (let i = 0; i < alerts.length; i++) {
            const index = this.alerts.findIndex((item) => item.id === alerts[i].id);

            if (index >= 0) {
                this.alerts.splice(index, 1, alerts[i]);
            }
        }

        return alerts;
    }

    private initAlertsCreationTask(createAlertEveryMs: number): Observable<null> {
        return interval(createAlertEveryMs).pipe(
            tap(() => {
                this.createAlert();
            }),
            mapTo(null),
        );
    }

    private createAlert(): void {
        const alert = alertDtoFactory.item({
            inserted: this.newAlertCreatedTime.valueOf(),
        });

        this.newAlertCreatedTime.add(1, 'h');
        this.alerts.push(alert);
    }
}

export class AlertsTemplatesBackendStore {
    private templates: AlertTemplateDTO[] = [];

    constructor(options: { total: number }) {
        for (let index = 0; index < options.total; index++) {
            this.createAlertTemplate();
        }
    }

    public getTemplates(params: TemplatesListSearchParams): TemplatesList {
        let templates = this.templates.slice();

        if ((params.sort as unknown as TemplatesListSortType[]).includes('id,asc')) {
            templates = templates.sort((a, b) => a.id - b.id);
        }

        const offset = params.offset * params.limit;

        return {
            count: templates.length,
            alertsTemplates: templates.slice(offset, offset + params.limit).map((template) => ({ ...template })),
        };
    }

    private createAlertTemplate(): void {
        const template = alertTemplateFactory.item();

        this.templates.push(template);
    }
}

export class AlertsTemplateRulesBackendStore {
    private rules: TemplateRuleDto[] = [];

    constructor(options: { total: number }) {
        for (let index = 0; index < options.total; index++) {
            this.createAlertTemplateRule({
                ruleType: sample(Object.values(RuleType)),
                ruleEntityType: sample(Object.values(TemplateRuleEntityType)),
                taxNumber: `1005060${Math.floor(Math.random() * 120)}`,
            });
        }
    }

    private createAlertTemplateRule(rule: CreateTemplateRuleDto): void {
        const newRule: Pick<TemplateRuleDto, 'id' | 'insertedAtUtc'> = alertTemplateRuleFactory.item();

        this.rules.unshift({ ...newRule, ...rule });
    }

    createAlertTemplateRules(params: string): void {
        const data: CreateTemplateRulesParams = JSON.parse(params);
        if (data.hasOwnProperty('clear') && data.clear) {
            this.rules = [];
        }

        data.rules.map((rule) => {
            this.createAlertTemplateRule({
                ruleType: rule.ruleType,
                ruleEntityType: rule.ruleEntityType,
                taxNumber: rule.taxNumber,
            });
        });
    }

    getAlertTemplateRules(params: TemplateRulesListSearchParams): TemplateRulesListDto {
        let rules = this.rules.slice();
        const offset = params.offset * params.limit;

        if (params.ruleType) {
            rules = rules.filter((rule) => {
                return params.ruleType === rule.ruleType;
            });
        }

        if (params.ruleEntityType) {
            rules = rules.filter((rule) => {
                return params.ruleEntityType === rule.ruleEntityType;
            });
        }

        if (params.taxNumber) {
            const searchString = params.taxNumber.toLowerCase().trim();

            rules = rules.filter((rule) => rule.taxNumber.toLowerCase().includes(searchString));
        }

        return {
            count: rules.length,
            rules: rules.slice(offset, offset + params.limit).map((rule) => ({ ...rule })),
        };
    }

    deleteTemplateRules(rulesIds: string, all: boolean = false): void {
        const ids: number[] = JSON.parse(rulesIds);

        if (!all) {
            ids.map((id) => {
                const index = this.rules.findIndex((rule) => rule.id === id);

                if (index !== -1) {
                    this.rules.splice(index, 1);
                }
            });
        } else {
            this.rules = [];
        }
    }

    updateTemplateRule(id: number, params: string): void {
        const data: UpdateTemplateRuleDto = JSON.parse(params);

        this.rules = this.rules.reduce((acc, rule) => {
            if (rule.id === id) {
                return [...acc, { ...rule, ruleType: data.ruleType }];
            }

            return [...acc, rule];
        }, []);
    }
}
